import React, { useEffect, useState } from 'react';
import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Header from '../components/Header';
import PaysGrid from '../components/PaysGrid';
import CardSkeleton from '../components/CardSkeleton';
import { useSnackbar } from '../components/SnackbarProvider';
import { recupererListeVisas, recupererListePays, recupererListeVoyageurs } from '../Services';
import { Visas } from '../models/Visas';
import { handleFlagError } from '../utils/flag';
import { toInfosPaysUrl } from '../utils/infosPays';

export interface Pays {
  id: number;
  libelle: string;
  visible: string;
  url_infos: string;
  url_flag: string;
  url_image: string;
}

const VisasScreen: React.FC = () => {
  const { t, i18n } = useTranslation('screens');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const [pays, setPays] = useState<Pays[]>([]);
  const [paysVisibles, setPaysVisibles] = useState<Pays[]>([]);
  const [paysSelectionne, setPaysSelectionne] = useState<number | null>(null);
  const [visasFiltres, setVisasFiltres] = useState<Visas[]>([]);
  const [urlInfoDestination, setUrlInfoDestination] = useState<string | null>(null);
  const [isParticulier] = useState<boolean>(false);
  const [isLoadingPays, setIsLoadingPays] = useState<boolean>(true);
  const [isLoadingVisas, setIsLoadingVisas] = useState<boolean>(false);
  const [filtreType, setFiltreType] = useState<'tous' | 'affaire' | 'tourisme'>('tous');
  const [openTooltipId, setOpenTooltipId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [paysRecents, setPaysRecents] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('recentPays');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const getUrlInfos = () => {
    try {
      const url = localStorage.getItem('pays_url_infos');
      if (url) {
        setUrlInfoDestination(url);
      }
    } catch (e) {
      console.error("Erreur lors de la récupération de l'URL depuis localStorage:", e);
    }
  };

  const handlePaysSelect = (paysId: number | null) => {
    const selectedPays = pays.find((p) => p.id === paysId);
    if (selectedPays) {
      setPaysSelectionne(selectedPays.id);
      try {
        localStorage.setItem('pays_url_infos', toInfosPaysUrl(selectedPays.url_infos) + '?appli=yes');
        console.log('URL du pays sauvegardée dans localStorage:', selectedPays.url_infos);
        getUrlInfos();
      } catch (e) {
        console.error("Erreur lors de la sauvegarde de l'URL dans localStorage:", e);
      }
      // Save to recent countries list
      try {
        const updated = [selectedPays.id, ...paysRecents.filter((id) => id !== selectedPays.id)].slice(0, 4);
        setPaysRecents(updated);
        localStorage.setItem('recentPays', JSON.stringify(updated));
      } catch (e) {
        console.error('Erreur lors de la sauvegarde des pays récents:', e);
      }
    }
  };

  // Navigation en fonction du titre du visa et du champ "classique"
  const navigateBasedOnTitle = (
    title: string,
    prixconsulat: number,
    prixintervention: number,
    url_flag: string,
    validite: string,
    informations: string,
    classique: number,
    commentaires: string,
    titleDisplay: string
  ) => {
    if (classique === 1 && title.includes('Arabie Saoudite')) {
      navigate('/classique_formulaire_arabie_saoudite', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Bangladesh')) {
      navigate('/classique_formulaire_bangladesh', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Centrafrique')) {
      navigate('/classique_formulaire_centrafrique', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Congo Brazzaville')) {
      navigate('/classique_formulaire_congo_brazzaville', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Congo Kinshasa')) {
      navigate('/classique_formulaire_congo_kinshasa', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Gabon')) {
      navigate('/classique_formulaire_gabon', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Ghana')) {
      navigate('/classique_formulaire_ghana', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Liban')) {
      navigate('/classique_formulaire_liban', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Libéria')) {
      navigate('/classique_formulaire_Liberia', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Népal')) {
      navigate('/classique_formulaire_Nepal', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Soudan')) {
      navigate('/classique_formulaire_Soudan', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Chine') && !title.includes('Chine eCard')) {
      navigate('/classique_formulaire_Chine', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Ethiopie')) {
      navigate('/classique_formulaire_Ethiopie', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Guinée Bissau')) {
      navigate('/classique_formulaire_guinee_bissau', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Guinée Equatoriale')) {
      navigate('/classique_formulaire_guinee_equatoriale', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && title.includes('Madagascar')) {
      navigate('/classique_formulaire_madagascar', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    if (classique === 1 && (title.includes('Nigéria') || title.includes('Nigeria'))) {
      navigate('/classique_formulaire_nigeria', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay },
      });
      return;
    }
    else if (title.includes('Chine eCard')) {
      navigate('/formulaire_chine_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Inde eCard')) {
      navigate('/formulaire_inde_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Inde')) {
      navigate('/formulaire_inde', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Nigéria') || title.includes('Nigeria')) {
      navigate('/formulaire_nigeria', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('USA')) {
      navigate('/formulaire_usa', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Angola')) {
      navigate('/formulaire_angola', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Arabie Saoudite')) {
      navigate('/formulaire_arabie_saoudite', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Australie')) {
      navigate('/formulaire_australie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Azerbaidjan')) {
      navigate('/formulaire_azerbaidjan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Bahreïn')) {
      navigate('/formulaire_bahrein', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Benin')) {
      navigate('/formulaire_benin', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Burkina Faso')) {
      navigate('/formulaire_burkina_faso', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Cambodge')) {
      navigate('/formulaire_cambodge', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Cameroun')) {
      navigate('/formulaire_cameroun', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Canada')) {
      navigate('/formulaire_canada', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Cap-Vert')) {
      navigate('/formulaire_cap_vert', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes('Corée Du Sud')) {
      navigate('/formulaire_coree_du_sud', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Cuba")) {
      navigate('/formulaire_cuba', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Djibouti")) {
      navigate('/formulaire_djibouti', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Dubai")) {
      navigate('/formulaire_dubai', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Egypte")) {
      navigate('/formulaire_egypte', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Ethiopie")) {
      navigate('/formulaire_ethiopie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Gabon")) {
      navigate('/formulaire_gabon', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Géorgie")) {
      navigate('/formulaire_georgie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Guinée") && !title.includes("Papouasie")) {
      navigate('/formulaire_guinee', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Indonésie")) {
      navigate('/formulaire_indonesie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Jordanie")) {
      navigate('/formulaire_jordanie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Kenya")) {
      navigate('/formulaire_kenya', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Kirghizistan")) {
      navigate('/formulaire_kirghizistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Koweït")) {
      navigate('/formulaire_koweit', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Laos")) {
      navigate('/formulaire_laos', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Lesotho")) {
      navigate('/formulaire_lesotho', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Madagascar")) {
      navigate('/formulaire_madagascar', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Malawi")) {
      navigate('/formulaire_malawi', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Mongolie")) {
      navigate('/formulaire_mongolie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Mozambique")) {
      navigate('/formulaire_mozambique', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Myanmar")) {
      navigate('/formulaire_myanmar', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Nouvelle-Zélande")) {
      navigate('/formulaire_nouvelle_zelande', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Oman")) {
      navigate('/formulaire_oman', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Ouganda")) {
      navigate('/formulaire_ouganda', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Ouzbékistan")) {
      navigate('/formulaire_ouzbekistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Pakistan")) {
      navigate('/formulaire_pakistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Papouasie")) {
      navigate('/formulaire_papouasie_ng', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("République Dominicaine")) {
      navigate('/formulaire_republique_dominicaine', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Seychelles")) {
      navigate('/formulaire_seychelles', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Sierra Leone")) {
      navigate('/formulaire_sierra_leone', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Singapour")) {
      navigate('/formulaire_singapour', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Sri Lanka")) {
      navigate('/formulaire_sri_lanka', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Tadjikistan")) {
      navigate('/formulaire_tadjikistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Tanzanie")) {
      navigate('/formulaire_tanzanie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Thailande")) {
      navigate('/formulaire_thailande', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Vietnam")) {
      navigate('/formulaire_vietnam', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Zambie")) {
      navigate('/formulaire_zambie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Zimbabwe")) {
      navigate('/formulaire_zimbabwe', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Togo")) {
      navigate('/formulaire_togo', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Turquie")) {
      navigate('/formulaire_turquie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else if (title.includes("Royaume-Uni") || title.includes("UK")) {
      navigate('/formulaire_uk', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    }
    else if (title.includes("Côte d'Ivoire")) {
      navigate('/formulaire_cote_d_ivoire', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    } else {
      navigate('/formulaire_template', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires, titleDisplay } });
    }
  };

  const checkVoyageursAndNavigate = async (
    title: string,
    prixconsulat: number,
    prixintervention: number,
    url_flag: string,
    validite: string,
    informations: string,
    classique: number,
    commentaires: string,
    titleDisplay: string
  ) => {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
      if (listeVoyageurs.length > 0) {
        navigateBasedOnTitle(title, prixconsulat, prixintervention, url_flag, validite, informations, classique, commentaires, titleDisplay);
      } else {
        showSnackbar(t('visas.errors.noTraveler'), 'warning');
        navigate('/formulaire_creation_voyageur');
      }
    } else {
      showSnackbar(t('visas.errors.userNotFound'), 'error');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Re-fetch pays à chaque changement de langue (i18n.language) : sinon le state
  // garde les libellés de la langue initiale.
  useEffect(() => {
    const fetchPays = async () => {
      setIsLoadingPays(true);
      const paysListe = await recupererListePays();
      setPays(paysListe);
      const visiblePays = paysListe.filter((p) => p.visible === 'oui');
      setPaysVisibles(visiblePays);
      setIsLoadingPays(false);
    };
    fetchPays();
  }, [i18n.language]);

  useEffect(() => {
    const fetchVisasFiltres = async () => {
      if (paysSelectionne !== null) {
        setIsLoadingVisas(true);
        const visasListe = await recupererListeVisas();
        const filtered = visasListe.filter((v) => v.id_pays === paysSelectionne);
        setVisasFiltres(filtered);
        setIsLoadingVisas(false);
      }
    };
    fetchVisasFiltres();
  }, [paysSelectionne, i18n.language]);

  const handleRetourPays = () => {
    setPaysSelectionne(null);
    setFiltreType('tous');
    setSearchQuery('');
  };

  const getVisasFiltresParType = () => {
    if (filtreType === 'tous') {
      return visasFiltres;
    } else if (filtreType === 'affaire') {
      return visasFiltres.filter((v) => v.titre_fr.toLowerCase().includes('affaire'));
    } else if (filtreType === 'tourisme') {
      return visasFiltres.filter((v) => v.titre_fr.toLowerCase().includes('tourisme'));
    }
    return visasFiltres;
  };

  const visasAffiches = getVisasFiltresParType();

  // Non-visible countries
  const paysNonVisibles = pays.filter((p) => p.visible !== 'oui');

  // Filter countries by search query (search across ALL countries)
  const query = searchQuery.trim().toLowerCase();
  const paysRecherches = query
    ? paysVisibles.filter((p) => p.libelle.toLowerCase().includes(query))
    : paysVisibles;
  const paysAutresRecherches = query
    ? paysNonVisibles.filter((p) => p.libelle.toLowerCase().includes(query))
    : paysNonVisibles;

  // Get selected country info
  const selectedPaysInfo = pays.find((p) => p.id === paysSelectionne);

  const openInfosPays = () => {
    if (urlInfoDestination) {
      window.open(urlInfoDestination, '_blank');
    } else {
      showSnackbar(t('visas.errors.noCountryUrl'), 'info');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(180deg, #FFF8EC 0%, #FAF3E2 100%)',
        backgroundColor: 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflowY: 'auto',
      }}
    >
      {/* Header (mobile only - returns null on desktop) */}
      <Header />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {paysSelectionne ? (
          /* ========== VUE VISAS (pays sélectionné) ========== */
          <>
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3,
                p: { xs: 1.5, sm: 2 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {/* Row 1: Back + Country info + Info button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: { xs: 'unset', sm: 1 },
                }}
              >
                <IconButton
                  onClick={handleRetourPays}
                  sx={{
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    width: 40,
                    height: 40,
                    '&:hover': { backgroundColor: '#B5811F' },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>

                {selectedPaysInfo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={selectedPaysInfo.url_flag}
                      alt={selectedPaysInfo.libelle}
                      onError={handleFlagError}
                      style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 2 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.15rem' },
                        color: '#333',
                      }}
                    >
                      {selectedPaysInfo.libelle}
                    </Typography>
                  </Box>
                )}

                <IconButton
                  onClick={openInfosPays}
                  sx={{
                    ml: 'auto',
                    color: '#2e7d32',
                    display: { xs: 'flex', sm: 'none' },
                  }}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Box>

              {/* Filters */}
              <ToggleButtonGroup
                value={filtreType}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setFiltreType(newValue);
                  }
                }}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  border: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                  },
                  '& .MuiToggleButton-root': {
                    px: 2.5,
                    py: 0.8,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    borderRadius: '8px !important',
                    flex: { xs: 1, sm: 'none' },
                    minWidth: { xs: 'auto', sm: '90px' },
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: '#2e7d32',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                      '&:hover': {
                        backgroundColor: '#B5811F',
                      },
                    },
                    '&:not(.Mui-selected)': {
                      color: '#666',
                      '&:hover': {
                        backgroundColor: '#ebebeb',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="tous">{t('visas.filters.all')}</ToggleButton>
                <ToggleButton value="affaire">{t('visas.filters.business')}</ToggleButton>
                <ToggleButton value="tourisme">{t('visas.filters.tourism')}</ToggleButton>
              </ToggleButtonGroup>

              {/* Infos Pays button (desktop only) */}
              <Button
                variant="outlined"
                startIcon={<InfoOutlinedIcon />}
                onClick={openInfosPays}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#B5811F',
                    backgroundColor: 'rgba(46,125,50,0.04)',
                  },
                }}
              >
                {t('visas.countryInfoBtn')}
              </Button>
            </Box>

            {/* Visa Cards Grid */}
            {isLoadingVisas ? (
              <CardSkeleton count={4} variant="visa" />
            ) : (
            <Grid container spacing={2}>
              {visasAffiches.map((item, index) => {
                const isEVisa = item.titre_fr.toLowerCase().includes('evisa');
                const isECard = item.titre_fr.toLowerCase().includes('ecard');
                const isClassique = item.classique === 1;
                const tooltipMessage = isEVisa
                  ? t('visas.tooltips.evisa')
                  : t('visas.tooltips.classic');

                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: isDesktop ? 'translateY(-4px)' : 'none',
                          boxShadow: isDesktop
                            ? '0 8px 30px rgba(0,0,0,0.15)'
                            : '0 2px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                      onClick={() =>
                        checkVoyageursAndNavigate(
                          // titre FR canonique : sert au routing vers le bon formulaire
                          // et finit en `libelle_visa` (FR) dans la table `demandes`.
                          item.titre_fr,
                          item.prixconsulat ?? 0,
                          item.prixintervention ?? 0,
                          item.url,
                          item.validite,
                          item.informations ?? '',
                          item.classique ?? 0,
                          item.commentaires ?? '',
                          item.titre
                        )
                      }
                    >
                      {/* Badge eVisa / Classique */}
                      {!isECard && (
                        <Chip
                          label={isClassique ? t('visas.badges.classic') : t('visas.badges.evisa')}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            zIndex: 1,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            backgroundColor: isClassique ? '#ff9800' : '#2e7d32',
                            color: 'white',
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ position: 'relative', mb: 1 }}>
                          <Typography
                            variant="h6"
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.95rem', sm: '1.05rem' },
                              color: '#333',
                              px: 3,
                            }}
                          >
                            {item.titre}
                          </Typography>
                          {!isECard && (
                            <Tooltip
                              title={tooltipMessage}
                              arrow
                              placement="top"
                              open={openTooltipId === index}
                              onClose={() => setOpenTooltipId(null)}
                              disableHoverListener
                              disableFocusListener
                              disableTouchListener
                            >
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  color: 'primary.main',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenTooltipId(openTooltipId === index ? null : index);
                                }}
                              >
                                <HelpOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ color: '#666', mb: 1.5 }}
                        >
                          {item.validite}
                        </Typography>

                        <CardMedia
                          component="img"
                          image={item.url}
                          alt={item.titre}
                          sx={{
                            height: { xs: 120, sm: 140, md: 150 },
                            objectFit: 'contain',
                            my: 1,
                            borderRadius: 2,
                          }}
                        />

                        {!isParticulier && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 2,
                              mt: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              label={t('visas.pricing.visa', { price: item.prixconsulat })}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderColor: '#2e7d32',
                                color: '#2e7d32',
                              }}
                            />
                            <Chip
                              label={t('visas.pricing.service', { price: item.prixintervention })}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderColor: '#D89D2F',
                                color: '#D89D2F',
                              }}
                            />
                          </Box>
                        )}

                        {item.informations && (
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{
                              display: 'block',
                              mt: 1.5,
                              color: '#888',
                              lineHeight: 1.4,
                            }}
                          >
                            {item.informations}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            )}
          </>
        ) : (
          /* ========== VUE SELECTION DE PAYS ========== */
          <>
            {/* Recently viewed countries */}
            {paysRecents.length > 0 && (
              <Box sx={{ maxWidth: { xs: '100%', sm: 600 }, mx: 'auto', mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#1A1A1A',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: '0.9rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('visas.recentlyViewed')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.4)', borderRadius: 2 },
                  }}
                >
                  {paysRecents.map((recentId) => {
                    const recentPays = pays.find((p) => p.id === recentId);
                    if (!recentPays) return null;
                    return (
                      <Box
                        key={recentId}
                        onClick={() => handlePaysSelect(recentId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: '#ffffff',
                          border: '1px solid #ECE4D3',
                          borderRadius: 2,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(26, 26, 26, 0.06)',
                          '&:hover': {
                            backgroundColor: '#FFF1D6',
                            borderColor: '#D89D2F',
                          },
                        }}
                      >
                        <img
                          src={recentPays.url_flag}
                          alt={recentPays.libelle}
                          onError={handleFlagError}
                          style={{ width: 22, height: 15, objectFit: 'cover', borderRadius: 2 }}
                        />
                        <Typography
                          sx={{
                            color: '#1A1A1A',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                          }}
                        >
                          {recentPays.libelle}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Search + Dropdown area */}
            <Box
              sx={{
                maxWidth: { xs: '100%', sm: 600 },
                mx: 'auto',
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Search bar */}
              <TextField
                placeholder={t('visas.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(8px)',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: '2px solid #2e7d32' },
                  },
                }}
              />

              {/* Country dropdown */}
              <TextField
                select
                label={t('visas.selectCountry')}
                value={paysSelectionne || ''}
                onChange={(e) => handlePaysSelect(Number(e.target.value))}
                fullWidth
                SelectProps={{
                  sx: {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 3,
                  },
                  renderValue: (value: unknown) => {
                    const selectedPays = pays.find((p) => p.id === value);
                    if (selectedPays) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={selectedPays.url_flag}
                            alt={selectedPays.libelle}
                            onError={handleFlagError}
                            style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }}
                          />
                          <span>{selectedPays.libelle}</span>
                        </Box>
                      );
                    }
                    return '';
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                  },
                }}
              >
                {pays.map((paysItem) => (
                  <MenuItem key={paysItem.id} value={paysItem.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={paysItem.url_flag}
                        alt={paysItem.libelle}
                        onError={handleFlagError}
                        style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }}
                      />
                      <span>{paysItem.libelle}</span>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Countries Grid */}
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              {isLoadingPays ? (
                <CardSkeleton count={8} variant="pays" />
              ) : (
                <PaysGrid paysVisibles={paysRecherches} paysAutres={paysAutresRecherches} onSelectPays={handlePaysSelect} />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default VisasScreen;
